import React from 'react';
import {
  ProductOptionsProvider,
  ProductPrice,
  Image,
  Link,
  OptionWithValues,
  useProductOptions,
  AddToCartButton,
} from '@shopify/hydrogen';
import {
  Image as ImageProps,
  Maybe,
  ProductVariant,
  CurrencyCode as CurrencyCodeType,
} from '@shopify/hydrogen/storefront-api-types';
import * as R from 'ramda';
import clsx from 'clsx';
// import Cookies from 'js-cookie';

//components
import Text from './Text.client';
// import ProductOptionsColor from '../product/options/ProductOptionsColor.client';
// import ProductOptions from '../product/options/ProductOptions.client';
import ProductPrices from './ProductPrices.client';
import ProductOptionSize from './ProductOptionSize.client';

//types
import type {SanityShopifyProduct, ProductWithNodes} from '../../types';

//utils
import {hasMultipleProductOptions} from '../../utils/productOptions';

interface Props {
  storefrontProduct: ProductWithNodes;
  sanityProduct?: SanityShopifyProduct;
}

const Wrapper: React.FC<Props> = ({storefrontProduct, sanityProduct}) => {
  const firstAvailableVariant = storefrontProduct.variants.nodes.find(
    (v) => v.availableForSale,
  );
  const multipleProductOptions = hasMultipleProductOptions(
    storefrontProduct.options,
  );

  const allColourOptions =
    multipleProductOptions &&
    storefrontProduct?.options?.find((o) => o.name === 'Colour')?.values;

  const allColourOptionsWithThumbnail =
    allColourOptions &&
    allColourOptions.map((colour) => ({
      colour: colour,
      thumbnail: storefrontProduct.variants.nodes.find((v) =>
        v.title.includes(colour),
      )?.image,
    }));

  return (
    <ProductOptionsProvider
      data={storefrontProduct}
      initialVariantId={firstAvailableVariant?.id}
    >
      <Product
        sanityProduct={sanityProduct}
        storefrontProduct={storefrontProduct}
        allColourOptionsWithThumbnail={allColourOptionsWithThumbnail}
      />
    </ProductOptionsProvider>
  );
};

interface ColourOptionsWithThumbnail {
  colour: string;
  thumbnail: Maybe<ImageProps> | undefined;
  available?: boolean;
  sizes?: {size: string | undefined; available: boolean}[];
}

const Product: React.FC<
  Props & {
    allColourOptionsWithThumbnail?: ColourOptionsWithThumbnail[] | boolean;
  }
> = ({storefrontProduct, sanityProduct, allColourOptionsWithThumbnail}) => {
  const multipleProductOptions = hasMultipleProductOptions(
    storefrontProduct.options,
  );
  const {options, selectedVariant} = useProductOptions();

  const productImage = selectedVariant?.image?.url
    ? selectedVariant?.image?.url
    : sanityProduct?.store.previewImageUrl
    ? sanityProduct?.store.previewImageUrl
    : '';

  const productAltText = selectedVariant?.image?.altText
    ? selectedVariant?.image?.altText
    : sanityProduct?.store.title;

  const availableVariants = storefrontProduct.variants.nodes.filter(
    (v) => v.availableForSale,
  );
  return (
    <li
      className={clsx(
        'product-variant h-full list-none',
        // allSoldOut && 'opacity-50 pointer-events-none',
      )}
      title={storefrontProduct.title}
    >
      <div className="flex h-full flex-col justify-between">
        <div className="">
          <div className="group">
            <Link to={`/products/${storefrontProduct.handle}`}>
              <div className="relative">
                <div className="aspect-w-16 aspect-h-16 overflow-hidden border border-slate-100 shadow-md transition-all duration-200 ease-out group-hover:shadow-lg">
                  <div className="p-4 transition-transform duration-200 ease-out group-hover:scale-110">
                    <Image
                      data={{
                        url: productImage,
                        altText: productAltText ?? 'Product Image',
                      }}
                      width={450}
                      height={450}
                    />
                  </div>
                </div>
                <div className="sizes absolute bottom-0 left-0 w-full px-2 pb-2">
                  {multipleProductOptions &&
                    (options as OptionWithValues[])?.map(
                      ({name, values}, index) => {
                        // only render the colour variant options
                        switch (name) {
                          case 'Size':
                            return (
                              <ProductOptionSize
                                key={`${name}-${index}-${storefrontProduct.handle}-size`}
                                name={name}
                                values={values}
                                allColourOptionsWithThumbnail={undefined}
                                availableVariants={availableVariants}
                              />
                            );
                          default:
                            return null;
                        }
                      },
                    )}
                </div>
              </div>

              <Text
                tag="h5"
                type="paragraph"
                className="line-clamp-3 relative mt-4 mb-2 min-h-[69.25px] md:min-h-[74.25px] text-center"
              >
                {storefrontProduct.title}
              </Text>
            </Link>
          </div>

          <div className="">
            {multipleProductOptions &&
              (options as OptionWithValues[])?.map(({name, values}, index) => {
                // only render the colour variant options
                switch (name) {
                  case 'Colour':
                    return (
                      <ProductOptionsColour
                        key={`${name}-${index}-${storefrontProduct?.handle}`}
                        name={name}
                        values={values}
                        allColourOptionsWithThumbnail={
                          allColourOptionsWithThumbnail
                        }
                        availableVariants={availableVariants}
                      />
                    );
                  default:
                    return null;
                }
              })}
          </div>
        </div>

        <div className="">
          <ProductPrices storefrontProduct={storefrontProduct} type="shopify"/>
          <ProductAddToCart />
        </div>
      </div>
    </li>
  );
};

function ProductOptionsColour({
  name,
  values,
  allColourOptionsWithThumbnail,
  availableVariants,
}: {
  name: string;
  values: string[];
  availableVariants: ProductVariant[];
  allColourOptionsWithThumbnail?: ColourOptionsWithThumbnail[] | boolean;
}) {
  const {setSelectedOption, selectedOptions} = useProductOptions();

  const handleChange = (optionName: string, optionValue: string) => {
    if (setSelectedOption) {
      setSelectedOption(optionName, optionValue);
    }
  };

  // First check if the available variants contains this colour
  // Then check what sizes are available for this colour

  const availableColours = R.uniq(
    availableVariants?.map(
      (v) => v.selectedOptions?.find((o) => o.name === 'Colour')?.value,
    ),
  );

  return (
    <fieldset key={name} className="my-2">
      {/* Name */}
      <ul className="grid grid-cols-5 gap-2">
        {values.map((value, index) => {
          const checked = selectedOptions?.[name] === value;
          const id = `option-${name}-${value}`;

          const thumbnail =
            allColourOptionsWithThumbnail &&
            Array.isArray(allColourOptionsWithThumbnail) &&
            allColourOptionsWithThumbnail?.find(
              (option) => option?.colour === value,
            )?.thumbnail;

          const colourIsAvailable = availableColours?.includes(value);

          const availableVariantsOfThisColour = availableVariants?.filter(
            (v) =>
              v.selectedOptions.find((o) => o?.name === 'Colour')?.value ===
              value,
          );

          const firstAvailableSize = availableVariantsOfThisColour
            .find((v) => v.selectedOptions?.find((o) => o?.name === 'Size'))
            ?.selectedOptions.find((o) => o?.name === 'Size')?.value;

          return (
            <li
              key={`${id}-${index}`}
              className={clsx(
                'product-variant group',
                !colourIsAvailable && 'pointer-events-none opacity-50',
              )}
            >
              {/* <Link to={`/products/`}> */}
              <div
                className="product-variant__image aspect-w-16 aspect-h-16 overflow-hidden"
                title={value}
              >
                <div className="transition-transform duration-200 ease-out">
                  <button
                    onClick={() => {
                      handleChange(name, value);
                      //select firstAvailable Size
                      if (setSelectedOption && firstAvailableSize) {
                        setSelectedOption('Size', firstAvailableSize);
                      }
                    }}
                  >
                    {thumbnail && (
                      <Image
                        data={{
                          url: thumbnail.url ?? '',
                          altText: name ?? '',
                        }}
                        width={150}
                        height={150}
                        className={clsx(
                          'rounded-full border-2',
                          checked
                            ? 'border-black'
                            : 'border-slate-300 border-opacity-50',
                        )}
                      />
                    )}
                    {/* <OptionButton checked={checked} label={value} /> */}
                  </button>
                </div>
              </div>
              {/* </Link> */}
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
}

function ProductAddToCart() {
  const {selectedVariant} = useProductOptions();

  return (
    <div className="">
      <div className="flex w-full ">
        <AddToCartButton
          className={clsx(
            'flex-1 rounded-md rounded-tl-md rounded-bl-md py-[15px] transition-opacity duration-200 ease-out hover:opacity-75',
            !selectedVariant?.availableForSale
              ? 'bg-hk-deep-gray'
              : 'bg-hk-orange',
          )}
          disabled={!selectedVariant?.availableForSale}
          quantity={1}
          variantId={selectedVariant?.id}
        >
          {selectedVariant?.availableForSale ? 'Add To Cart' : 'Out of Stock'}
        </AddToCartButton>
        {/* <button className="group flex items-center rounded-tr-md rounded-br-md border-[1px] border-hk-orange bg-white px-3">
          <svg
            width="24"
            height="21"
            viewBox="0 0 24 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="transition-transform duration-200 ease-out group-hover:scale-125"
          >
            <path
              d="M12.2278 1.91298C10.9943 0.773553 9.37974 0.141166 7.70404 0.141113C6.8171 0.142041 5.93911 0.319164 5.12058 0.662288C4.30205 1.00541 3.55914 1.50776 2.93462 2.14043C0.270851 4.8278 0.271983 9.03115 2.93688 11.7071L11.2372 20.0456C11.4297 20.3856 11.801 20.6051 12.2278 20.6051C12.403 20.6034 12.5754 20.5605 12.7312 20.4797C12.8869 20.3989 13.0216 20.2825 13.1244 20.14L21.5187 11.7071C24.1836 9.03001 24.1836 4.8278 21.5164 2.13588C20.8922 1.50438 20.1499 1.00305 19.3322 0.660712C18.5145 0.318379 17.6374 0.141795 16.7516 0.141113C15.0759 0.141388 13.4614 0.773745 12.2278 1.91298ZM19.9157 3.74398C21.6851 5.53063 21.6863 8.32149 19.918 10.099L12.2278 17.8245L4.53763 10.099C2.76933 8.32149 2.77047 5.53063 4.53537 3.74853C5.39574 2.88876 6.52102 2.41565 7.70404 2.41565C8.88705 2.41565 10.0078 2.88876 10.8614 3.74626L11.4274 4.31489C11.5325 4.4206 11.6572 4.50446 11.7946 4.56167C11.9319 4.61889 12.0791 4.64834 12.2278 4.64834C12.3765 4.64834 12.5237 4.61889 12.661 4.56167C12.7984 4.50446 12.9231 4.4206 13.0282 4.31489L13.5942 3.74626C15.3059 2.03012 18.2063 2.03467 19.9157 3.74398Z"
              fill="#FFA500"
            />
          </svg>
        </button> */}
      </div>
    </div>
  );
}

export default Wrapper;
